import { useCallback } from 'react'

import { useUnit } from 'effector-react'

import CloseIcon from '@/assets/icons/icon-close.svg'
import ExitIcon from '@/assets/icons/icon-exit.svg'
import TrashIcon from '@/assets/icons/icon-trash.svg'
import { Button } from '@/components/ui/button.tsx'
import { DIALOG_TYPE } from '@/dialogs/constants.ts'
import { openDialog } from '@/models/dialogs.ts'
import { $hasEditVaultPermission } from '@/models/permissions.ts'
import { $selectedMediaIds, resetSelectedMedia } from '@/models/vault/medias'
import { IconButton } from '@/modules/chatting/ui/components/icon-button.tsx'
import { MAX_MEDIA_SELECTION_LIMIT } from '@/modules/vault/consts.ts'
import { classNames } from '@/utils/helpers.ts'

interface Props {
  hasDeleteMediaPermission: boolean
  showMediaActions: boolean
}

export const VaultMediaActions = ({
  hasDeleteMediaPermission,
  showMediaActions,
}: Props) => {
  const hasEditVaultPermission = useUnit($hasEditVaultPermission)
  const selectedMediaIds = useUnit($selectedMediaIds)
  const hasSelectedMedia = !!selectedMediaIds.length

  const handleDeleteOpen = useCallback(() => {
    openDialog({
      type: DIALOG_TYPE.DELETE_MEDIA,
    })
  }, [])

  const handleMediaMoveDialogOpen = useCallback(() => {
    openDialog({
      type: DIALOG_TYPE.MOVE_MEDIA,
    })
  }, [])

  return (
    <div
      className={classNames(
        'dar:bg-secondary-600 bg-card pointer-events-none absolute bottom-4 left-4 z-10 flex w-[calc(100%-32px)] items-center justify-between rounded-[100px] p-2 opacity-0 shadow-lg duration-200',
        {
          'pointer-events-auto opacity-95': hasSelectedMedia,
        },
      )}
    >
      <div className="flex items-center">
        <Button
          onClick={resetSelectedMedia}
          variant="ghost"
          className="text-primary-500 mr-1"
        >
          <CloseIcon className="h-5 w-5" />
        </Button>
        <p className="text-sm font-medium">
          {selectedMediaIds.length} / {MAX_MEDIA_SELECTION_LIMIT} Selected
        </p>
      </div>
      <div className="flex items-center gap-4 mr-4">
        {showMediaActions && hasEditVaultPermission && (
          <Button
            size="sm"
            onClick={handleMediaMoveDialogOpen}
            className="gap-2"
          >
            <ExitIcon /> Add to Category
          </Button>
        )}

        {showMediaActions && hasDeleteMediaPermission && (
          <IconButton
            icon={TrashIcon}
            tooltip="Delete media"
            onClick={handleDeleteOpen}
            className="text-red-600 hover:text-red-500"
            iconClassName="h-5 w-5"
          />
        )}
      </div>
    </div>
  )
}
